import React from "react";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import InputIcon from "@mui/icons-material/Input";
import CheckIcon from "@mui/icons-material/Check";
import SyncIcon from "@mui/icons-material/Sync";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { buildLuxorPromptMessage } from "../utils/buildLuxorPromptMessage";

interface TextEditorProps {
  importedContent: string;
  freeText: string;
  onImportedChange: (value: string) => void;
  onFreeTextChange: (value: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  onImport: () => void;
  imported?: boolean;
  isImportedDirty?: boolean;
}

export const TextEditor: React.FC<TextEditorProps> = ({
  importedContent,
  freeText,
  onImportedChange,
  onFreeTextChange,
  onSubmit,
  isLoading,
  onImport,
  imported = false,
  isImportedDirty = false,
}) => {
  const importedRef = React.useRef<HTMLDivElement>(null);
  const promptMessage = buildLuxorPromptMessage(importedContent, freeText);
  const isImportedEmpty = !importedContent.trim();

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (promptMessage.trim()) {
        onSubmit();
      }
    }
  };

  React.useEffect(() => {
    if (importedRef.current) {
      const textareaElement = importedRef.current.querySelector("textarea");
      if (textareaElement) {
        textareaElement.scrollTop = 0;
      }
    }
  }, [importedContent]);

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      fontSize: 16,
      "&.Mui-focused fieldset": {
        borderColor: "primary.main",
        borderWidth: 0.5,
      },
    },
  };

  const importButtonLabel = imported
    ? "Importado!"
    : isImportedEmpty
      ? "Importar dados"
      : "Atualizar dados";

  const ImportAction = (
    <Tooltip
      title="Importa anamnese, exames, condutas e demais informações já registradas neste atendimento"
      placement="top"
    >
      <span>
        <Button
          size="small"
          variant={isImportedEmpty ? "contained" : "outlined"}
          disabled={imported}
          startIcon={
            imported ? (
              <CheckIcon sx={{ fontSize: 16 }} />
            ) : isImportedEmpty ? (
              <InputIcon sx={{ fontSize: 16 }} />
            ) : (
              <SyncIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={onImport}
          sx={{
            flexShrink: 0,
            textTransform: "none",
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            px: 1.5,
            py: 0.5,
            whiteSpace: "nowrap",
            ...(isImportedEmpty
              ? {
                  bgcolor: "#00ae9a",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#009688", boxShadow: 1 },
                }
              : {
                  borderColor: imported ? "success.light" : "primary.light",
                  color: imported ? "success.main" : "primary.main",
                  bgcolor: imported ? "rgba(76, 175, 80, 0.06)" : "white",
                  "&:hover": {
                    bgcolor: "rgba(0, 174, 154, 0.08)",
                    borderColor: "primary.main",
                  },
                }),
          }}
        >
          {importButtonLabel}
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 2, display: "block" }}
      >
        Shift+Enter para nova linha · Enter para enviar
      </Typography>

      {/* Região 1 — Dados do atendimento */}
      <Box
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: isImportedEmpty ? "primary.light" : "divider",
          borderStyle: isImportedEmpty ? "dashed" : "solid",
          overflow: "hidden",
          bgcolor: "neutral100.main",
          transition: "border-color 0.2s, border-style 0.2s",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            px: 2,
            py: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "rgba(0, 174, 154, 0.05)",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexWrap: "wrap",
              }}
            >
              <DescriptionOutlinedIcon
                sx={{ fontSize: 18, color: "primary.main" }}
              />
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.primary"
              >
                Dados do atendimento
              </Typography>
              {isImportedDirty && (
                <Chip
                  label="Editado manualmente"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem", height: 22 }}
                />
              )}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25, pl: 3.25 }}
            >
              {isImportedEmpty
                ? "Importe os registros do atendimento para enviar à IA"
                : "Importados automaticamente · você pode editar antes de enviar"}
            </Typography>
          </Box>
          {ImportAction}
        </Box>

        {isImportedEmpty ? (
          <Box
            sx={{
              px: 2,
              py: 3,
              minHeight: 160,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(0, 174, 154, 0.1)",
                mb: 0.5,
              }}
            >
              <InputIcon sx={{ color: "primary.main", fontSize: 24 }} />
            </Box>
            <Typography variant="body2" fontWeight={500} color="text.primary">
              Nenhum dado importado ainda
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ maxWidth: 320 }}
            >
              Anamnese, dados objetivos, problemas, condutas e intervenções
              aparecerão aqui após a importação
            </Typography>
          </Box>
        ) : (
          <TextField
            value={importedContent}
            onChange={(e) => onImportedChange(e.target.value)}
            multiline
            minRows={8}
            maxRows={12}
            fullWidth
            onKeyPress={handleKeyPress}
            variant="outlined"
            ref={importedRef}
            sx={{
              ...textFieldSx,
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                fontSize: 16,
                bgcolor: "transparent",
                "& fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: "none" },
              },
            }}
          />
        )}
      </Box>

      {/* Separador visual */}
      <Divider sx={{ my: 2 }}>
        <MoreHorizIcon sx={{ color: "text.disabled", fontSize: 20 }} />
      </Divider>

      {/* Região 2 — Texto livre */}
      <Box>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          color="text.primary"
          sx={{ mb: 0.5 }}
        >
          Complementos e instruções
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1.5, display: "block" }}
        >
          Perguntas específicas, contexto adicional ou orientações para a IA.
        </Typography>
        <TextField
          value={freeText}
          onChange={(e) => onFreeTextChange(e.target.value)}
          placeholder="Ex.: Foque no diagnóstico diferencial de..."
          multiline
          minRows={4}
          maxRows={8}
          fullWidth
          onKeyPress={handleKeyPress}
          variant="outlined"
          sx={textFieldSx}
          InputProps={{
            sx: {
              bgcolor: "white",
            },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!promptMessage.trim() || isLoading}
          sx={{
            bgcolor: "#00ae9a",
            color: "white",
            fontWeight: 600,
            borderRadius: 2,
            py: 1,
            px: 3,
            boxShadow: 1,
            "&:hover": {
              bgcolor: "#009688",
              boxShadow: 2,
            },
            transition: "all 0.2s",
          }}
          endIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )
          }
        >
          {isLoading ? "Enviando..." : "Enviar"}
        </Button>
      </Box>
    </Box>
  );
};
